import { Component } from '@theme/component';
import {
  center,
  closest,
  clamp,
  getVisibleElements,
  mediaQueryLarge,
  isDesktopBreakpoint,
  prefersReducedMotion,
  preventDefault,
  viewTransition,
} from '@theme/utilities';
import { Scroller, scrollIntoView } from '@theme/scrolling';
import { SlideshowSelectEvent } from '@theme/events';

// The threshold for determining visibility of slides.
const SLIDE_VISIBLITY_THRESHOLD = 0.7;

/**
 * Slideshow custom element that allows sliding between content.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [current]
 * @property {HTMLElement[]} [thumbnails]
 * @property {HTMLElement[]} [dots]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class Slideshow extends Component {
  requiredRefs = ['scroller', 'slideshowContainer'];

  async connectedCallback() {
    super.connectedCallback();

    // Wait for any in-progress view transitions to finish
    if (viewTransition.current) {
      await viewTransition.current;
      if (!this.isConnected) return;
    }

    const { scroller } = this.refs;
    this.#scroll = new Scroller(scroller, {
      onScroll: this.#handleScroll,
      onScrollStart: this.#onTransitionInit,
      onScrollEnd: this.#onTransitionEnd,
    });

    scroller.addEventListener('mousedown', this.#handleMouseDown);
    scroller.addEventListener('touchstart', this.#handleTouchStart, { passive: false });

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('pointerenter', this.#handlePointerEnter);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#updateControlsVisibility();
    this.#updateArrowsVisibility();

    this.disabled = this.isNested || this.disabled;

    this.resume();

    this.current = this.dataset.initialSlide ? parseInt(this.dataset.initialSlide, 10) : 0;

    if (this.hasAttribute('fade-effect')) {
      this.#initializeFadeStates();
    }

    const draggableDesktop =
      (this.hasAttribute('draggable-desktop') ? this.getAttribute('draggable-desktop') === 'true' : true);

    this.draggable = isDesktopBreakpoint() && draggableDesktop;

    let visibleSlidesAmount = 0;

    if (this.current !== 0) {
      this.select(this.current, undefined, { animate: false });
      visibleSlidesAmount = 1;
    } else {
      visibleSlidesAmount = this.#updateVisibleSlides();
      if (visibleSlidesAmount === 0) {
        this.select(0, undefined, { animate: false });
        visibleSlidesAmount = 1;
      }
    }

    this.#resizeObserver = new ResizeObserver(async () => {
      if (viewTransition.current) await viewTransition.current;

      if (visibleSlidesAmount > 1) {
        this.#updateVisibleSlides();
      }

      if (this.hasAttribute('auto-hide-controls')) {
        this.#updateControlsVisibility();
      }
      this.#updateArrowsVisibility();
    });

    this.#resizeObserver.observe(this.refs.slideshowContainer);

    document.addEventListener(SlideshowSelectEvent.eventName, () => {
      this.#updateArrowsVisibility();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const { scroller } = this.refs;

    scroller.removeEventListener('mousedown', this.#handleMouseDown);
    scroller.removeEventListener('touchstart', this.#handleTouchStart);
    this.removeEventListener('mouseenter', this.suspend);
    this.removeEventListener('mouseleave', this.resume);
    this.removeEventListener('pointerenter', this.#handlePointerEnter);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#scroll?.destroy();

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }
  }

  /** Indicates whether the slideshow is nested inside another slideshow. */
  get isNested() {
    return this.parentElement?.closest('slideshow-component') !== null;
  }

  get defaultSlide() {
    return this.refs.slides?.[this.initialSlideIndex];
  }

  /**
   * Selects a slide based on the input index.
   * @param {number|string|{id: string}} input - The index or id of the slide to select.
   * @param {Event} [event] - The event that triggered the selection.
   * @param {Object} [options] - The options for the selection.
   * @param {boolean} [options.animate=true] - Whether to animate the selection.
   */
  async select(input, event, options = {}) {
    if (this.#disabled || !this.refs.slides?.length) return;

    for (const slide of this.refs.slides) {
      if (slide.hasAttribute('reveal')) {
        slide.removeAttribute('reveal');
        slide.setAttribute('aria-hidden', 'true');
      }
    }

    let requestedIndex = (() => {
      if (typeof input === 'number') return input;
      if (typeof input === 'string') return parseInt(input, 10);
      if ('id' in input) {
        const requestedSlide = this.refs.slides.find((slide) => slide.getAttribute('slide-id') == input.id);

        if (!requestedSlide || !this.slides) return;

        if (requestedSlide.hasAttribute('hidden')) {
          requestedSlide.setAttribute('reveal', '');
          requestedSlide.setAttribute('aria-hidden', 'false');
        }

        return this.slides.indexOf(requestedSlide);
      }
    })();

    if (requestedIndex === undefined || isNaN(requestedIndex)) return;

    const { slides } = this;
    if (!slides?.length) return;

    event?.preventDefault();

    const { current } = this;
    const { animate = true } = options;
    const lastIndex = slides.length - 1;

    let index = requestedIndex;
    if (this.infinite) {
      if (requestedIndex < 0) index = lastIndex;
      else if (requestedIndex > lastIndex) index = 0;
    } else {
      index = clamp(requestedIndex, 0, lastIndex);
    }

    const stepSize = this.#getStepSize();
    const isNormalStep = Math.abs(requestedIndex - current) <= stepSize && requestedIndex >= 0 && requestedIndex <= lastIndex;
    const isWrapAround = this.infinite && (
      (requestedIndex === 0 && current === lastIndex) ||
      (requestedIndex === lastIndex && current === 0)
    );
    const isAdjacentSlide = isNormalStep || isWrapAround;
    const { visibleSlides } = this;
    const instant = prefersReducedMotion() || !animate;

    // Only use this trick for single-slide slideshows
    if (!instant && !isAdjacentSlide && visibleSlides.length === 1) {
      this.#disabled = true;
      await this.#scroll.finished;

      const targetSlide = slides[index];
      const currentSlide = slides[current];
      if (!targetSlide || !currentSlide) return;

      const placeholder = document.createElement('slideshow-slide');
      targetSlide.before(placeholder);

      if (requestedIndex < current) {
        currentSlide.before(targetSlide);
      } else {
        currentSlide.after(targetSlide);
      }

      if (current === 0) this.#scroll.to(currentSlide, { instant: true });

      queueMicrotask(async () => {
        await this.#scroll.finished;
        this.#disabled = false;

        placeholder.replaceWith(targetSlide);
        this.#scroll.to(targetSlide, { instant: true });
      });
    }

    const slide = slides[index];
    if (!slide) return;

    const previousIndex = this.current;

    slide.setAttribute('aria-hidden', 'false');

    // FADE: Always ensure correct fade state for the new current slide
    if (this.hasAttribute('fade-effect')) {
      // First, set all slides: visible for the new current, hidden for the rest
      slides.forEach((s, i) => {
        if (i === index) {
          s.setAttribute('data-fade-state', 'visible');
        } else {
          s.setAttribute('data-fade-state', 'hidden');
        }
      });
    }

    this.#scroll.to(slide, { instant });
    this.current = this.slides?.indexOf(slide) || 0;

    this.#centerSelectedThumbnail(index, instant ? 'instant' : 'smooth');
    this.#updateArrowsVisibility();

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: event != null,
        trigger: 'select',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  }

  /**
   * Advances to the next slide.
   * @param {Event} [event] - The event that triggered the next slide.
   * @param {Object} [options] - The options for the next slide.
   * @param {boolean} [options.animate=true] - Whether to animate the next slide.
   */
  next(event, options) {
    event?.preventDefault();
    this.select(this.nextIndex, event, options);
  }

  /**
   * Goes back to the previous slide.
   * @param {Event} [event] - The event that triggered the previous slide.
   * @param {Object} [options] - The options for the previous slide.
   * @param {boolean} [options.animate=true] - Whether to animate the previous slide.
   */
  previous(event, options) {
    event?.preventDefault();
    this.select(this.previousIndex, event, options);
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (this.#interval) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      if (this.hasAttribute('fade-effect')) {
        this.nextWithFade();
      } else {
        this.next();
      }
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    if (value) {
      this.setAttribute('paused', '');
    } else {
      this.removeAttribute('paused');
    }
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  /**
   * Advances to the next slide with fade transition.
   * @param {Event} [event] - The event that triggered the next slide.
   * @param {Object} [options] - The options for the next slide.
   * @param {boolean} [options.animate=true] - Whether to animate the next slide.
   */
  nextWithFade(event, options = {}) {
    if (this.#disabled || !this.refs.slides?.length) return;

    const { slides } = this;
    if (!slides) return;
    
    const currentSlide = slides[this.current];
    const nextSlide = slides[this.nextIndex];

    if (!currentSlide || !nextSlide) {
      this.next(event, options);
      return;
    }

    // Fixed fade duration of 200ms for each phase (400ms total)
    const fadeDuration = this.#calculateFadeDuration();
    
    // Set fade duration CSS variable
    this.style.setProperty('--fade-duration', `${fadeDuration}ms`);

    // Start fade out of current slide
    currentSlide.setAttribute('data-fade-state', 'fading-out');
    
    // Wait for fade out to complete, then switch slides and fade in
    setTimeout(() => {
      // Move to next slide
      this.next(event, { ...options, animate: false });
      
      // Start fade in of next slide
      nextSlide.setAttribute('data-fade-state', 'fading-in');
      
      // Clean up fade states after fade in completes
      setTimeout(() => {
        currentSlide.removeAttribute('data-fade-state');
        nextSlide.setAttribute('data-fade-state', 'visible');
      }, fadeDuration);
    }, fadeDuration);
  }

  /**
   * Calculates the fade duration for each phase (fade-out and fade-in).
   * @returns {number} The fade duration in milliseconds for each phase (200ms each = 400ms total).
   */
  #calculateFadeDuration() {
    // Fixed fade duration of 200ms for each phase (400ms total)
    return 200;
  }

  /**
   * Initializes fade states for all slides.
   */
  #initializeFadeStates() {
    const { slides } = this;
    if (!slides) return;

    slides.forEach((slide, index) => {
      if (index === this.current) {
        slide.setAttribute('data-fade-state', 'visible');
      } else {
        slide.setAttribute('data-fade-state', 'hidden');
      }
    });
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  /**
   * The current slide index.
   * @type {number}
   */
  #current = 0;

  get current() {
    return this.#current;
  }

  /**
   * Sets the current slide index and update the DOM
   * @type {number}
   */
  set current(value) {
    const { current, thumbnails, dots, slides, previous, next } = this.refs;

    this.#current = value;

    if (current) current.textContent = `${value + 1}`;

    for (const controls of [thumbnails, dots]) {
      controls?.forEach((el, i) => el.setAttribute('aria-selected', `${i === value}`));
    }

    if (previous) previous.disabled = Boolean(!this.infinite && value === 0);
    if (next) next.disabled = Boolean(!this.infinite && slides && this.nextIndex >= slides.length);

    // FADE: Always update fade states after index change
    if (this.hasAttribute('fade-effect') && slides) {
      slides.forEach((slide, i) => {
        if (i === value) {
          slide.setAttribute('data-fade-state', 'visible');
        } else {
          slide.setAttribute('data-fade-state', 'hidden');
        }
      });
    }
  }

  get infinite() {
    return this.getAttribute('infinite') != null;
  }

  get visibleSlides() {
    return getVisibleElements(this.refs.scroller, this.slides, SLIDE_VISIBLITY_THRESHOLD, 'x');
  }

  /**
   * Gets the step size for navigation
   * @returns {number} The step size to use for navigation
   */
  #getStepSize() {
    const mobileMediaQuery = window.matchMedia('(max-width: 767px)');
    const stepSize = this.getAttribute('step-size');
    const stepSizeMobile = this.getAttribute('step-size-mobile');
    const customStep = mobileMediaQuery.matches ? (stepSizeMobile ?? stepSize) : stepSize;

    if (customStep) {
      return Math.max(1, parseInt(customStep, 10) || 1);
    }
    if (this.infinite) {
      return 1;
    }
    return this.visibleSlides.length > 1 ? this.visibleSlides.length : 1;
  }

  get previousIndex() {
    const { current, slides } = this;
    const stepSize = this.#getStepSize();
    const rawIndex = current - stepSize;
    
    if (!slides?.length) return rawIndex;
    
    if (this.infinite) {
      return rawIndex < 0 ? slides.length - 1 : rawIndex;
    }
    
    return Math.max(0, rawIndex);
  }

  get nextIndex() {
    const { current, slides } = this;
    const stepSize = this.#getStepSize();
    const rawIndex = current + stepSize;
    
    if (!slides?.length) return rawIndex;
    
    if (this.infinite) {
      return rawIndex >= slides.length ? 0 : rawIndex;
    }
    
    return Math.min(slides.length - 1, rawIndex);
  }

  get atStart() {
    const { current, slides } = this;
    if (!slides?.length || this.infinite) return false;
    return current === 0; 
  }

  get atEnd() {
    const { current, slides } = this;
    if (!slides?.length || this.infinite) return false;
    return current === slides.length - 1;
  }

  /**
   * Sets the disabled attribute.
   * @param {boolean} value - The value to set the disabled attribute to.
   */
  set disabled(value) {
    this.setAttribute('disabled', String(value));
  }
  /**
   * Whether the slideshow is disabled.
   * @type {boolean}
   */
  get disabled() {
    return (
      this.getAttribute('disabled') === 'true' || (this.hasAttribute('mobile-disabled') && !mediaQueryLarge.matches)
    );
  }

  /**
   * Indicates whether the slideshow is temporarily disabled (e.g., during infinite loop transition).
   * @type {boolean}
   */
  #disabled = false;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * The Scroller instance that manages scrolling.
   * @type {Scroller}
   */
  #scroll;

  /**
   * The ResizeObserver instance for monitoring scroller size changes
   * @type {ResizeObserver}
   */
  #resizeObserver;

  /**
   * Callback invoked on user initiated scroll to sync the current slide index
   * and emit a slide change event if the index has changed.
   */
  #handleScroll = () => {
    const previousIndex = this.#current;
    const index = this.#sync();

    if (index === previousIndex) return;

    const slide = this.slides?.[index];
    if (!slide) return;

    // FADE: Always update fade state on scroll (including drag), so the new visible slide is marked.
    if (this.hasAttribute('fade-effect') && this.slides) {
      this.slides.forEach((s, i) => {
        if (i === index) {
          s.setAttribute('data-fade-state', 'visible');
        } else {
          s.setAttribute('data-fade-state', 'hidden');
        }
      });
    }

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: true,
        trigger: 'scroll',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  };

  #onTransitionInit = () => {
    this.setAttribute('transitioning', '');
  };

  #onTransitionEnd = () => {
    this.#updateVisibleSlides();
    this.#updateArrowsVisibility();
    this.removeAttribute('transitioning');
  };

  /**
   * Synchronizes the scroll position and updates the current slide index.
   * @returns {number} The index of the current slide.
   */
  #sync = () => {
    const { slides } = this;
    if (!slides) return (this.current = 0);

    const visibleSlides = this.visibleSlides;

    if (!visibleSlides.length) return this.current;

    const { axis } = this.#scroll;
    const { scroller } = this.refs;
    const centers = visibleSlides.map((slide) => center(slide, axis));
    const referencePoint = visibleSlides.length > 1 ? scroller.getBoundingClientRect()[axis] : center(scroller, axis);
    const closestCenter = closest(centers, referencePoint);
    const closestVisibleSlide = visibleSlides[centers.indexOf(closestCenter)];

    if (!closestVisibleSlide) return (this.current = 0);

    const index = slides.indexOf(closestVisibleSlide);

    return (this.current = index);
  };

  #dragging = false;

  /**
   * Handles the 'mousedown' event to start dragging slides.
   * @param {MouseEvent} event - The mousedown event.
   */
  #handleMouseDown = (event) => {
    const { slides } = this;

    if (!slides || slides.length <= 1) return;
    if (!(event.target instanceof Element)) return;
    if (this.disabled || this.#dragging || !this.draggable) return;

    if (event.target.closest('model-viewer')) {
      return;
    }

    event.preventDefault();
    const { axis } = this.#scroll;
    const startPosition = event[axis];

    const controller = new AbortController();
    const { signal } = controller;
    const startTime = performance.now();
    let previous = startPosition;
    let velocity = 0;
    let moved = false;
    let distanceTravelled = 0;

    this.#dragging = true;

    const onPointerMove = (event) => {
      const current = event[axis];
      const initialDelta = startPosition - current;

      if (!initialDelta) return;

      if (!moved) {
        moved = true;
        this.setPointerCapture(event.pointerId);

        document.addEventListener('click', preventDefault, { once: true, signal });

        const movingRight = initialDelta < 0;
        const movingLeft = initialDelta > 0;

        const closestSlideshow = this.parentElement?.closest('slideshow-component');
        const isNested = closestSlideshow instanceof Slideshow && closestSlideshow !== this;
        const cannotMoveInDirection = (movingRight && this.atStart) || (movingLeft && this.atEnd);

        if (isNested && cannotMoveInDirection) {
          controller.abort();
          return;
        }

        this.pause();
        this.setAttribute('dragging', '');
      }

      event.stopImmediatePropagation();

      const delta = previous - current;
      const timeDelta = performance.now() - startTime;
      velocity = Math.round((delta / timeDelta) * 1000);
      previous = current;
      distanceTravelled += Math.abs(delta);

      this.#scroll.by(delta, { instant: true });
    };

    const onPointerUp = async (event) => {
      controller.abort();
      const { current, slides } = this;
      const { scroller } = this.refs;

      this.#dragging = false;

      if (!slides?.length || !scroller) return;

      const direction = Math.sign(velocity);
      const next = this.#sync();

      const modifier = current !== next || Math.abs(velocity) < 10 || distanceTravelled < 10 ? 0 : direction;
      const newIndex = clamp(next + modifier, 0, slides.length - 1);

      const newSlide = slides[newIndex];
      const currentIndex = this.current;

      if (!newSlide) throw new Error(`Slide not found at index ${newIndex}`);

      this.#scroll.to(newSlide);

      this.removeAttribute('dragging');
      this.releasePointerCapture(event.pointerId);

      this.#centerSelectedThumbnail(newIndex);

      // FADE: Set data-fade-state for slides during drag release
      if (this.hasAttribute('fade-effect') && slides) {
        slides.forEach((slide, i) => {
          if (i === newIndex) {
            slide.setAttribute('data-fade-state', 'visible');
          } else {
            slide.setAttribute('data-fade-state', 'hidden');
          }
        });
      }

      this.dispatchEvent(
        new SlideshowSelectEvent({
          index: newIndex,
          previousIndex: currentIndex,
          userInitiated: true,
          trigger: 'drag',
          slide: newSlide,
          id: newSlide.getAttribute('slide-id'),
        })
      );

      this.current = newIndex;

      await this.#scroll.finished;

      if (this.#dragging) return;

      this.#scroll.snap = true;
      this.resume();
    };

    this.#scroll.snap = false;

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    document.addEventListener('pointerdown', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
    document.addEventListener('pointercapturelost', onPointerUp, { signal });
  };

  /**
   * Handles the 'touchstart' event to start dragging slides on mobile.
   * @param {TouchEvent} event - The touchstart event.
   */
  #handleTouchStart = (event) => {
    const { slides } = this;

    if (!slides || slides.length <= 1) return;
    if (!(event.target instanceof Element)) return;
    if (this.disabled || this.#dragging) return;

    if (event.target.closest('model-viewer')) {
      return;
    }

    if (event.touches.length !== 1) return;

    const { axis } = this.#scroll;
    const touch = event.touches[0];
    if (!touch) return;
    const startPosition = axis === 'x' ? touch.clientX : touch.clientY;

    const controller = new AbortController();
    const { signal } = controller;
    const startTime = performance.now();
    let previous = startPosition;
    let velocity = 0;
    let moved = false;
    let distanceTravelled = 0;
    let startX = touch.clientX;
    let startY = touch.clientY;

    const tapTimeout = setTimeout(() => {
      if (!moved) {
        controller.abort();
      }
    }, 150);

    const onTouchMove = (event) => {
      if (event.touches.length !== 1) return;
      
      const touch = event.touches[0];
      if (!touch) return;
      
      const currentX = touch.clientX;
      const currentY = touch.clientY;
      const deltaX = Math.abs(currentX - startX);
      const deltaY = Math.abs(currentY - startY);
      
      const minDragDistance = 10;
      const isHorizontalDrag = deltaX > deltaY && deltaX > minDragDistance;
      
      if (!isHorizontalDrag && !moved) {
        return;
      }
      
      const current = axis === 'x' ? currentX : currentY;
      const initialDelta = startPosition - current;

      if (!initialDelta) return;

      if (!moved) {
        moved = true;
        clearTimeout(tapTimeout);
        event.preventDefault();

        if (touch) {
          this.setPointerCapture(touch.identifier);
        }

        document.addEventListener('click', preventDefault, { once: true, signal });

        const movingRight = initialDelta < 0;
        const movingLeft = initialDelta > 0;

        const closestSlideshow = this.parentElement?.closest('slideshow-component');
        const isNested = closestSlideshow instanceof Slideshow && closestSlideshow !== this;
        const cannotMoveInDirection = (movingRight && this.atStart) || (movingLeft && this.atEnd);

        if (isNested && cannotMoveInDirection) {
          controller.abort();
          return;
        }

        this.pause();
        this.setAttribute('dragging', '');
        this.#dragging = true;
      }

      event.stopImmediatePropagation();

      const delta = previous - current;
      const timeDelta = performance.now() - startTime;
      velocity = Math.round((delta / timeDelta) * 1000);
      previous = current;
      distanceTravelled += Math.abs(delta);

      this.#scroll.by(delta, { instant: true });

      // FADE: Also update fade-state during touch drag for live feedback
      if (this.hasAttribute('fade-effect') && slides) {
        const currentSlideIndex = this.#sync();
        slides.forEach((s, i) => {
          if (i === currentSlideIndex) {
            s.setAttribute('data-fade-state', 'visible');
          } else {
            s.setAttribute('data-fade-state', 'hidden');
          }
        });
        this.#centerSelectedThumbnail(currentSlideIndex);
      }
    };

    const onTouchEnd = async (event) => {
      controller.abort();
      clearTimeout(tapTimeout);

      if (!moved) return;
      
      const { current, slides } = this;
      const { scroller } = this.refs;

      this.#dragging = false;
      this.removeAttribute('dragging');

      if (!slides?.length || !scroller) return;

      const { axis } = this.#scroll;
      const currentPosition = event.changedTouches[0] ? (axis === 'x' ? event.changedTouches[0].clientX : event.changedTouches[0].clientY) : previous;
      const totalDelta = startPosition - currentPosition;
      const totalTime = performance.now() - startTime;

      const finalVelocity = totalTime > 0 ? Math.round((totalDelta / totalTime) * 1000) : 0;

      const direction = Math.sign(finalVelocity);
      const next = this.#sync();

      const shouldSnap = Math.abs(finalVelocity) > 300 || distanceTravelled > 50;
      const modifier = current !== next || Math.abs(finalVelocity) < 10 || distanceTravelled < 10 ? 0 : direction;
      const newIndex = clamp(next + modifier, 0, slides.length - 1);

      const newSlide = slides[newIndex];
      const currentIndex = this.current;

      if (!newSlide) throw new Error(`Slide not found at index ${newIndex}`);

      this.#scroll.to(newSlide);

      // FADE: Set fade-state on touch slide-release as well
      if (this.hasAttribute('fade-effect') && slides) {
        slides.forEach((slide, i) => {
          if (i === newIndex) {
            slide.setAttribute('data-fade-state', 'visible');
          } else {
            slide.setAttribute('data-fade-state', 'hidden');
          }
        });
      }

      this.#centerSelectedThumbnail(newIndex);

      this.dispatchEvent(
        new SlideshowSelectEvent({
          index: newIndex,
          previousIndex: currentIndex,
          userInitiated: true,
          trigger: 'drag',
          slide: newSlide,
          id: newSlide.getAttribute('slide-id'),
        })
      );

      this.resume();
    };

    this.#scroll.snap = false;

    document.addEventListener('touchmove', onTouchMove, { signal, passive: false });
    document.addEventListener('touchend', onTouchEnd, { signal });
    document.addEventListener('touchcancel', onTouchEnd, { signal });
  };

  #handlePointerEnter = () => {
    this.setAttribute('actioned', '');
  };

  get slides() {
    return this.refs.slides?.filter((slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal'));
  }

  /**
   * The initial slide index.
   * @type {number}
   */
  get initialSlideIndex() {
    return this.dataset.initialSlide ? parseInt(this.dataset.initialSlide, 10) : 0;
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());

  #updateControlsVisibility() {
    if (!this.hasAttribute('auto-hide-controls')) return;

    const { scroller, slideshowControls } = this.refs;

    if (!(slideshowControls instanceof HTMLElement)) return;

    slideshowControls.hidden = scroller.scrollWidth <= scroller.offsetWidth;
  }

  /**
   * Updates the visibility of the slideshow arrows.
   */
  #updateArrowsVisibility() {
    if (!this.hasAttribute('auto-hide-arrows')) return;

    const { scroller, previous, next } = this.refs;

    if (!(previous instanceof HTMLElement) || !(next instanceof HTMLElement)) return;

    const canScroll = scroller.scrollWidth > scroller.clientWidth;

    previous.hidden = !canScroll;
    next.hidden = !canScroll;
  }

  /**
   * Centers the selected thumbnail in the thumbnails container
   * @param {number} index - The index of the selected thumbnail
   * @param {ScrollBehavior} [behavior] - The scroll behavior.
   */
  #centerSelectedThumbnail(index, behavior = 'smooth') {
    const selectedThumbnail = this.refs.thumbnails?.[index];
    if (!selectedThumbnail) return;

    const { thumbnailsContainer } = this.refs;
    if (!thumbnailsContainer || !(thumbnailsContainer instanceof HTMLElement)) return;

    const { slideshowControls } = this.refs;
    if (!slideshowControls || !(slideshowControls instanceof HTMLElement)) return;

    scrollIntoView(selectedThumbnail, {
      ancestor: thumbnailsContainer,
      behavior,
      block: 'center',
      inline: 'center',
    });
  }

  #updateVisibleSlides() {
    const { slides } = this;
    if (!slides || !slides.length) return 0;

    const visibleSlides = this.visibleSlides;

    slides.forEach((slide, i) => {
      const isVisible = visibleSlides.includes(slide);
      slide.setAttribute('aria-hidden', `${!isVisible}`);

      // FADE: synchronize data-fade-state for visible slides as well
      if (this.hasAttribute('fade-effect')) {
        slide.setAttribute('data-fade-state', isVisible ? 'visible' : 'hidden');
      }
    });

    return visibleSlides.length;
  }
}

if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', Slideshow);
}
